class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    const form = this.querySelector('form');

    if (form) {
      this.debouncedOnSubmit = debounce((event) => {
        this.onSubmitHandler(event);
      }, 500);

      form.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;

      if (searchParams === FacetFiltersForm.searchParamsPrev) return;

      FacetFiltersForm.renderPage(searchParams, null, false);
    };

    window.addEventListener('popstate', onHistoryChange);
  }

  static renderPage(searchParams, event = null, updateURLHash = true) {
    let targetSection = null;

    if (event && event.target) {
      targetSection = event.target.closest('.shopify-section');
    }

    if (!targetSection && event && event.target && event.target.closest('.filter-form')) {
      targetSection =
        document.querySelector('.collection-page-section') ||
        document.querySelector('.main-search') ||
        document.querySelector('.shopify-section');
    }

    if (!targetSection) {
      targetSection =
        document.querySelector('.collection-page-section') ||
        document.querySelector('.main-search') ||
        document.querySelector('[id^="ProductGridContainer--"]')?.closest('.shopify-section') ||
        document.querySelector('.shopify-section');
    }

    if (!targetSection) return;

    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections(targetSection);
    const countContainer = targetSection.querySelector('[id^="ProductCount--"]');
    const productGridContainer = targetSection.querySelector('[id^="ProductGridContainer--"]');

    if (productGridContainer) {
      productGridContainer.querySelector('.collection')?.classList.add('loading');
    }

    if (countContainer) {
      countContainer.classList.add('loading');
    }

    sections.forEach((section) => {
      if (!section.section) return;

      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, targetSection)
        : FacetFiltersForm.renderSectionFromFetch(url, event, targetSection);
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
    }

    document.dispatchEvent(new CustomEvent('page:reloaded'));
  }

  static renderSectionFromFetch(url, event, targetSection) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;

        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];

        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html, targetSection);
        FacetFiltersForm.renderProductCount(html, targetSection);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, targetSection) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;

    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html, targetSection);
    FacetFiltersForm.renderProductCount(html, targetSection);
  }

  static renderProductGridContainer(html, targetSection) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const newProductGridContainer = parsedHTML.querySelector('[id^="ProductGridContainer--"]');
    const currentProductGridContainer = targetSection.querySelector('[id^="ProductGridContainer--"]');

    if (!newProductGridContainer || !currentProductGridContainer) return;

    currentProductGridContainer.innerHTML = newProductGridContainer.innerHTML;

    const layoutSwitcher = targetSection.querySelector('#FacetFiltersForm layout-switcher');

    if (layoutSwitcher) {
      layoutSwitcher.onButtonClick(layoutSwitcher.querySelector('.list-view__item--active'));
    }
  }

  static renderProductCount(html, targetSection) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHTML.querySelector('[id^="ProductCount--"]');
    const container = targetSection.querySelector('[id^="ProductCount--"]');

    if (!sourceCount || !container) return;

    container.innerHTML = sourceCount.innerHTML;
    container.classList.remove('loading');
  }

  static renderFilters(html, event = null) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersForm-2 .js-filter'
    );

    const expandedFiltersIds = FacetFiltersForm.getExpandedFiltersIds();

    const matchesId = (element) => {
      if (!event || !event.target) return false;

      const jsFilter = event.target.closest('.js-filter');

      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesId);

    facetsToRender.forEach((element) => {
      const currentElement = document.querySelector(`.js-filter[id="${element.id}"]`);

      if (currentElement) {
        currentElement.innerHTML = element.innerHTML;
      }
    });

    document.dispatchEvent(new CustomEvent('filters:rerendered'));

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && event && event.target) {
      const currentJSFilter = event.target.closest('.js-filter');

      if (currentJSFilter && currentJSFilter.id) {
        FacetFiltersForm.renderCounts(countsToRender, currentJSFilter);

        const matchingFilter = document.querySelector(`[id="${currentJSFilter.id}"]`);

        if (matchingFilter) {
          FacetFiltersForm.renderMobileCounts(countsToRender, matchingFilter);
        }
      }
    }

    if (expandedFiltersIds.length > 0) {
      FacetFiltersForm.expandFilters(expandedFiltersIds);
    }
  }

  static expandFilters(expandedFiltersIds) {
    expandedFiltersIds.forEach((id) => {
      const filterElement = document.getElementById(id);

      if (!filterElement) return;

      const showMoreBtn = filterElement.querySelector('show-more-button');

      if (showMoreBtn) {
        showMoreBtn.toggleShowMore(showMoreBtn.querySelector('.button-show-more'), true);
      }
    });
  }

  static getExpandedFiltersIds() {
    const expandedFiltersIds = [];

    document.querySelectorAll('facet-filters-form show-more-button').forEach((btn) => {
      const showMoreLabel = btn.querySelector('.label-show--more');

      if (showMoreLabel && showMoreLabel.classList.contains('hidden')) {
        const parentFilter = btn.closest('.js-filter');

        if (parentFilter) {
          expandedFiltersIds.push(parentFilter.id);
        }
      }
    });

    return expandedFiltersIds;
  }

  static renderActiveFacets(html) {
    const isRTL = document.documentElement.dir === 'rtl';

    const activeFacetElementSelectors = [
      '.active-facets-mobile',
      '.active-facets-desktop',
      '.active-facets-desktop-2',
      '.active-facets--result',
      '.active-facets--result-2',
      '.results-toast--horizontal',
      '.results-toast--vertical',
      '.results-toast--drawer'
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const currentElement = document.querySelector(selector);

      if (!activeFacetsElement || !currentElement) return;

      currentElement.innerHTML = activeFacetsElement.innerHTML;
    });

    if (window.innerWidth <= 768 || document.querySelector('.drawer-filter')) {
      const resultsToast = document.querySelector('.results-toast--drawer');

      if (!resultsToast) return;

      const closestForm = resultsToast.closest('facet-filters-form');

      if (!closestForm) return;

      const positionRight = (closestForm.offsetWidth + 48) / 2;

      resultsToast.style.insetInlineEnd = `${positionRight}px`;
      resultsToast.style.transform = isRTL ? 'translate(-50%, -24px)' : 'translate(50%, -24px)';

      setTimeout(() => {
        resultsToast.style.transform = isRTL ? 'translate(-50%, 100px)' : 'translate(50%, 100px)';
      }, 3000);
    }

    if (
      (window.innerWidth > 768 && document.querySelector('.vertical-filter')) ||
      (window.innerWidth > 768 && document.querySelector('.horizontal-filter'))
    ) {
      const resultsToast =
        document.querySelector('.results-toast--horizontal') ||
        document.querySelector('.results-toast--vertical');

      if (!resultsToast) return;

      resultsToast.style.insetInlineEnd = '50%';
      resultsToast.style.transform = isRTL ? 'translate(-50%, -24px)' : 'translate(50%, -24px)';

      setTimeout(() => {
        resultsToast.style.transform = isRTL ? 'translate(-50%, 100px)' : 'translate(50%, 100px)';
      }, 3000);
    }
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.facets__open', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const sourceElement = html.querySelector(selector);
      const targetElement = document.querySelector(selector);

      if (!sourceElement || !targetElement) return;

      targetElement.innerHTML = sourceElement.innerHTML;
    });
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      targetElement.outerHTML = sourceElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`
    );
  }

  static getSections(targetSection = document) {
    const productGrid = targetSection.querySelector('[id^="product-grid--"]');

    if (!productGrid || !productGrid.dataset.id) {
      return [];
    }

    return [
      {
        section: productGrid.dataset.id
      }
    ];
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const form = event.target.closest('form');

    if (!form) return;

    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData).toString();

    FacetFiltersForm.renderPage(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();

    const href = event.currentTarget.getAttribute('href');

    if (!href) return;

    const searchParams = new URL(href, window.location.origin).searchParams.toString();

    FacetFiltersForm.renderPage(searchParams, event);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm);
}

FacetFiltersForm.setListeners();

class FacetRemove extends HTMLElement {
  constructor() {
    super();

    const link = this.querySelector('a');

    if (!link) return;

    link.addEventListener('click', (event) => {
      event.preventDefault();

      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');

      if (!form || typeof form.onActiveFilterClick !== 'function') return;

      form.onActiveFilterClick(event);
    });
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemove);
}

document.addEventListener('click', function (event) {
  const closeButton = event.target.closest('.js-ilimito-close-filters, .close-popup, .button-close');

  if (!closeButton) return;

  const drawer = closeButton.closest('.filter-form.popup-wrapper.ilimito-filters-drawer');

  if (!drawer) return;

  event.preventDefault();

  drawer.classList.remove('open');
  drawer.closest('.snippet-facets')?.classList.remove('open');
  document.documentElement.classList.remove('overflow-hidden');
  document.body.classList.remove('overflow-hidden');
});

document.addEventListener('click', function (event) {
  const openButton = event.target.closest('.open-popup');

  if (!openButton) return;

  const snippet = openButton.closest('.snippet-facets');
  const drawer =
    snippet?.querySelector('.filter-form.popup-wrapper.ilimito-filters-drawer') ||
    document.querySelector('.filter-form.popup-wrapper.ilimito-filters-drawer');

  if (!drawer) return;

  event.preventDefault();

  drawer.classList.add('open');
  drawer.closest('.snippet-facets')?.classList.add('open');
  document.documentElement.classList.add('overflow-hidden');
  document.body.classList.add('overflow-hidden');
});